(() => {
  const CONTROLLER_FLAG = 'data-hormonize-hero-js-ready';
  const HERO_SELECTOR = '.hormonize-hero';
  const SLIDER_SELECTOR = '[data-hormonize-review-slider]';
  const DESKTOP_MEDIA_QUERY = '(min-width: 990px)';
  const AUTOPLAY_INTERVAL_MS = 5000;
  const TRANSITION_MS = 450;
  const SWIPE_THRESHOLD_PX = 48;

  const instances = new WeakMap();
  const sliderInstances = new WeakMap();

  class HormonizeReviewSliderController {
    constructor(slider) {
      this.slider = slider;
      this.viewport = slider.querySelector('.hormonize-review-slider__viewport');
      this.track = slider.querySelector('.hormonize-review-slider__track');
      this.slides = Array.from(slider.querySelectorAll('.hormonize-review-slider__slide'));
      this.prevButton = slider.querySelector('[data-review-slider-prev]');
      this.nextButton = slider.querySelector('[data-review-slider-next]');
      this.pagination = slider.querySelector('[data-review-slider-pagination]');
      this.status = slider.querySelector('.hormonize-review-slider__status');
      this.slideCount = this.slides.length;
      this.activeIndex = 0;
      this.autoplayTimer = null;
      this.transitionTimer = null;
      this.isAnimating = false;
      this.isDragging = false;
      this.isPaused = false;
      this.pointerStartX = 0;
      this.pointerCurrentX = 0;
      this.dragOffsetPx = 0;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.desktopMediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);

      this.onPrevClick = this.onPrevClick.bind(this);
      this.onNextClick = this.onNextClick.bind(this);
      this.onPaginationClick = this.onPaginationClick.bind(this);
      this.onViewportKeyDown = this.onViewportKeyDown.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onPointerCancel = this.onPointerUp.bind(this);
      this.onMouseEnter = this.onMouseEnter.bind(this);
      this.onMouseLeave = this.onMouseLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onReducedMotionChange = this.onReducedMotionChange.bind(this);
      this.onDesktopMediaQueryChange = this.onDesktopMediaQueryChange.bind(this);

      if (!this.viewport || !this.track || this.slideCount < 2) {
        return;
      }

      this.buildPagination();
      this.bindEvents();
      this.goTo(0, { animate: false, userInitiated: false });
      this.startAutoplay();
    }

    get transitionDurationMs() {
      return this.reducedMotion.matches ? 0 : TRANSITION_MS;
    }

    get isInteractivePaused() {
      return this.isPaused || this.isDragging || this.isAnimating;
    }

    bindEvents() {
      this.prevButton?.addEventListener('click', this.onPrevClick);
      this.nextButton?.addEventListener('click', this.onNextClick);
      this.pagination?.addEventListener('click', this.onPaginationClick);
      this.viewport.addEventListener('keydown', this.onViewportKeyDown);
      this.viewport.addEventListener('pointerdown', this.onPointerDown);
      this.viewport.addEventListener('pointermove', this.onPointerMove);
      this.viewport.addEventListener('pointerup', this.onPointerUp);
      this.viewport.addEventListener('pointercancel', this.onPointerCancel);
      this.slider.addEventListener('mouseenter', this.onMouseEnter);
      this.slider.addEventListener('mouseleave', this.onMouseLeave);
      this.slider.addEventListener('focusin', this.onFocusIn);
      this.slider.addEventListener('focusout', this.onFocusOut);
      this.reducedMotion.addEventListener('change', this.onReducedMotionChange);
      this.desktopMediaQuery.addEventListener('change', this.onDesktopMediaQueryChange);
    }

    unbindEvents() {
      this.prevButton?.removeEventListener('click', this.onPrevClick);
      this.nextButton?.removeEventListener('click', this.onNextClick);
      this.pagination?.removeEventListener('click', this.onPaginationClick);
      this.viewport?.removeEventListener('keydown', this.onViewportKeyDown);
      this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport?.removeEventListener('pointermove', this.onPointerMove);
      this.viewport?.removeEventListener('pointerup', this.onPointerUp);
      this.viewport?.removeEventListener('pointercancel', this.onPointerCancel);
      this.slider?.removeEventListener('mouseenter', this.onMouseEnter);
      this.slider?.removeEventListener('mouseleave', this.onMouseLeave);
      this.slider?.removeEventListener('focusin', this.onFocusIn);
      this.slider?.removeEventListener('focusout', this.onFocusOut);
      this.reducedMotion.removeEventListener('change', this.onReducedMotionChange);
      this.desktopMediaQuery.removeEventListener('change', this.onDesktopMediaQueryChange);
    }

    buildPagination() {
      if (!this.pagination) {
        return;
      }

      this.pagination.innerHTML = '';

      this.slides.forEach((slide, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'hormonize-review-slider__dot';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', `Review ${index + 1}`);
        dot.setAttribute('aria-controls', slide.id);
        dot.dataset.slideIndex = String(index);
        this.pagination.appendChild(dot);
      });

      this.paginationButtons = Array.from(
        this.pagination.querySelectorAll('.hormonize-review-slider__dot')
      );
    }

    onPrevClick() {
      this.previous({ userInitiated: true });
    }

    onNextClick() {
      this.next({ userInitiated: true });
    }

    onPaginationClick(event) {
      const button = event.target.closest('[data-slide-index]');

      if (!(button instanceof HTMLButtonElement) || !this.pagination?.contains(button)) {
        return;
      }

      const index = Number(button.dataset.slideIndex);

      if (Number.isNaN(index)) {
        return;
      }

      this.goTo(index, { userInitiated: true });
    }

    onViewportKeyDown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.previous({ userInitiated: true });
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.next({ userInitiated: true });
      }
    }

    onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) {
        return;
      }

      this.isDragging = true;
      this.isPaused = true;
      this.stopAutoplay();
      this.pointerStartX = event.clientX;
      this.pointerCurrentX = event.clientX;
      this.dragOffsetPx = 0;
      this.viewport.setPointerCapture(event.pointerId);
      this.viewport.classList.add('is-dragging');
      this.track.classList.add('is-dragging');
      this.track.style.transition = 'none';
      this.applyTransform(this.activeIndex, this.dragOffsetPx);
    }

    onPointerMove(event) {
      if (!this.isDragging) {
        return;
      }

      this.pointerCurrentX = event.clientX;
      this.dragOffsetPx = this.pointerCurrentX - this.pointerStartX;
      this.applyTransform(this.activeIndex, this.dragOffsetPx);
    }

    onPointerUp(event) {
      if (!this.isDragging) {
        return;
      }

      this.isDragging = false;
      this.viewport.releasePointerCapture(event.pointerId);
      this.viewport.classList.remove('is-dragging');
      this.track.classList.remove('is-dragging');

      const viewportWidth = this.viewport.clientWidth || 1;
      const dragRatio = this.dragOffsetPx / viewportWidth;

      if (dragRatio <= -SWIPE_THRESHOLD_PX / viewportWidth) {
        this.next({ userInitiated: true });
      } else if (dragRatio >= SWIPE_THRESHOLD_PX / viewportWidth) {
        this.previous({ userInitiated: true });
      } else {
        this.goTo(this.activeIndex, { userInitiated: false, allowSameIndex: true });
      }

      this.resumeAutoplayAfterInteraction();
    }

    onMouseEnter() {
      if (!this.desktopMediaQuery.matches) {
        return;
      }

      this.isPaused = true;
      this.stopAutoplay();
    }

    onMouseLeave() {
      if (!this.desktopMediaQuery.matches) {
        return;
      }

      this.isPaused = false;
      this.startAutoplay();
    }

    onFocusIn() {
      this.isPaused = true;
      this.stopAutoplay();
    }

    onFocusOut(event) {
      if (this.slider.contains(event.relatedTarget)) {
        return;
      }

      this.isPaused = false;
      this.startAutoplay();
    }

    onReducedMotionChange() {
      this.stopAutoplay();

      if (!this.reducedMotion.matches) {
        this.startAutoplay();
      }

      this.goTo(this.activeIndex, { animate: false, userInitiated: false });
    }

    onDesktopMediaQueryChange() {
      if (!this.desktopMediaQuery.matches) {
        this.isPaused = false;
        this.startAutoplay();
      }
    }

    previous({ userInitiated = false } = {}) {
      const nextIndex = (this.activeIndex - 1 + this.slideCount) % this.slideCount;
      this.goTo(nextIndex, { direction: 'prev', userInitiated });
    }

    next({ userInitiated = false } = {}) {
      const nextIndex = (this.activeIndex + 1) % this.slideCount;
      this.goTo(nextIndex, { direction: 'next', userInitiated });
    }

    goTo(index, { direction = 'next', animate = true, userInitiated = false, allowSameIndex = false } = {}) {
      if (index < 0 || index >= this.slideCount || !this.track) {
        return;
      }

      if (index === this.activeIndex && !allowSameIndex) {
        return;
      }

      const previousIndex = this.activeIndex;
      const isWrapForward =
        direction === 'next' && previousIndex === this.slideCount - 1 && index === 0 && !allowSameIndex;
      const isWrapBackward =
        direction === 'prev' && previousIndex === 0 && index === this.slideCount - 1 && !allowSameIndex;
      const shouldAnimate = animate && this.transitionDurationMs > 0;

      window.clearTimeout(this.transitionTimer);
      this.isAnimating = true;

      if ((isWrapForward || isWrapBackward) && shouldAnimate) {
        this.animateWrap(index, userInitiated);
        return;
      }

      this.track.classList.remove('is-fading');
      this.track.style.transition = shouldAnimate
        ? `transform ${this.transitionDurationMs}ms ease, opacity ${this.transitionDurationMs}ms ease`
        : 'none';

      this.activeIndex = index;
      this.applyTransform(index, 0);
      this.updateSlideStates();
      this.updatePagination();

      if (userInitiated) {
        this.announceSlide(index);
      }

      this.transitionTimer = window.setTimeout(() => {
        this.isAnimating = false;

        if (!this.isInteractivePaused) {
          this.startAutoplay();
        }
      }, shouldAnimate ? this.transitionDurationMs : 0);
    }

    animateWrap(index, userInitiated) {
      this.track.classList.add('is-fading');
      this.track.style.transition = `opacity ${this.transitionDurationMs}ms ease`;

      window.requestAnimationFrame(() => {
        this.track.style.opacity = '0.35';

        this.transitionTimer = window.setTimeout(() => {
          this.track.style.transition = 'none';
          this.activeIndex = index;
          this.applyTransform(index, 0);
          this.updateSlideStates();
          this.updatePagination();
          this.track.style.opacity = '1';

          window.requestAnimationFrame(() => {
            this.track.style.transition = `opacity ${this.transitionDurationMs}ms ease`;
            this.track.style.opacity = '1';
            this.track.classList.remove('is-fading');

            if (userInitiated) {
              this.announceSlide(index);
            }

            this.transitionTimer = window.setTimeout(() => {
              this.isAnimating = false;

              if (!this.isInteractivePaused) {
                this.startAutoplay();
              }
            }, this.transitionDurationMs);
          });
        }, this.transitionDurationMs);
      });
    }

    applyTransform(index, dragOffsetPx) {
      const offsetPercent = index * 100;
      const dragPercent = this.viewport.clientWidth
        ? (dragOffsetPx / this.viewport.clientWidth) * 100
        : 0;

      this.track.style.transform = `translate3d(calc(-${offsetPercent}% + ${dragOffsetPx}px), 0, 0)`;
      this.track.style.opacity = '1';

      if (dragOffsetPx !== 0) {
        this.track.dataset.dragPercent = dragPercent.toFixed(2);
      } else {
        delete this.track.dataset.dragPercent;
      }
    }

    updateSlideStates() {
      this.slides.forEach((slide, index) => {
        const isActive = index === this.activeIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });
    }

    updatePagination() {
      if (!this.paginationButtons) {
        return;
      }

      this.paginationButtons.forEach((button, index) => {
        const isActive = index === this.activeIndex;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
        button.setAttribute('tabindex', isActive ? '0' : '-1');
      });
    }

    announceSlide(index) {
      if (!this.status) {
        return;
      }

      const slide = this.slides[index];
      const quote = slide?.querySelector('.hormonize-review-slider__quote')?.textContent?.trim();
      const author = slide?.querySelector('.hormonize-review-slider__author')?.textContent?.trim();

      this.status.textContent = [quote, author].filter(Boolean).join(' — ');
    }

    startAutoplay() {
      this.stopAutoplay();

      if (this.reducedMotion.matches || this.slideCount < 2 || this.isInteractivePaused) {
        return;
      }

      this.autoplayTimer = window.setInterval(() => {
        if (this.isInteractivePaused) {
          return;
        }

        this.next({ userInitiated: false });
      }, AUTOPLAY_INTERVAL_MS);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    resumeAutoplayAfterInteraction() {
      this.isPaused = false;
      this.startAutoplay();
    }

    destroy() {
      this.stopAutoplay();
      window.clearTimeout(this.transitionTimer);
      this.unbindEvents();
      this.slider = null;
      this.viewport = null;
      this.track = null;
      this.slides = [];
    }
  }

  class HormonizeHeroController {
    constructor(hero) {
      this.hero = hero;
      this.reviewSliders = [];

      hero.querySelectorAll(SLIDER_SELECTOR).forEach((slider) => {
        if (sliderInstances.has(slider)) {
          return;
        }

        const controller = new HormonizeReviewSliderController(slider);

        if (controller.viewport && controller.track && controller.slideCount >= 2) {
          sliderInstances.set(slider, controller);
          this.reviewSliders.push(controller);
        }
      });
    }

    destroy() {
      this.reviewSliders.forEach((controller) => {
        const slider = controller.slider;

        if (slider) {
          controller.destroy();
          sliderInstances.delete(slider);
        }
      });

      this.reviewSliders = [];
      this.hero = null;
    }
  }

  function initHero(hero) {
    if (!(hero instanceof HTMLElement) || !hero.matches(HERO_SELECTOR)) {
      return;
    }

    if (instances.has(hero)) {
      return;
    }

    instances.set(hero, new HormonizeHeroController(hero));
  }

  function initHeroes(root = document) {
    const searchRoot = root instanceof Element || root instanceof Document ? root : document;

    if (searchRoot instanceof Element && searchRoot.matches(HERO_SELECTOR)) {
      initHero(searchRoot);
    }

    searchRoot.querySelectorAll(HERO_SELECTOR).forEach(initHero);
  }

  function destroyHeroes(root) {
    const searchRoot = root instanceof Element ? root : document;
    const heroes = [];

    if (searchRoot instanceof Element && searchRoot.matches(HERO_SELECTOR)) {
      heroes.push(searchRoot);
    }

    searchRoot.querySelectorAll(HERO_SELECTOR).forEach((hero) => {
      heroes.push(hero);
    });

    heroes.forEach((hero) => {
      const instance = instances.get(hero);

      if (!instance) {
        return;
      }

      instance.destroy();
      instances.delete(hero);
    });
  }

  function onSectionLoad(event) {
    initHeroes(event.target);
  }

  function onSectionUnload(event) {
    destroyHeroes(event.target);
  }

  function bindLifecycleEvents() {
    document.addEventListener('shopify:section:load', onSectionLoad);
    document.addEventListener('shopify:section:unload', onSectionUnload);
    document.addEventListener('shopify:section:reorder', () => {
      destroyHeroes(document);
      initHeroes(document);
    });
    document.addEventListener('shopify:block:select', (event) => {
      const slider = event.target.closest(SLIDER_SELECTOR);

      if (!slider) {
        return;
      }

      const controller = sliderInstances.get(slider);
      const slide = event.target.closest('.hormonize-review-slider__slide');

      if (!controller || !(slide instanceof HTMLElement)) {
        return;
      }

      const index = Number(slide.dataset.slideIndex);

      if (!Number.isNaN(index)) {
        controller.goTo(index, { userInitiated: true });
      }
    });
  }

  if (!document.documentElement.hasAttribute(CONTROLLER_FLAG)) {
    document.documentElement.setAttribute(CONTROLLER_FLAG, '');
    bindLifecycleEvents();
  }

  initHeroes(document);
})();
